import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSeason } from "../context/SeasonContext";
import { useStandings } from "../hooks/useStandings";
import { getTeamStyle } from "../utils/teamColors";
import { PlayoffBracket } from "./PlayoffBracket";
import StandingsSeasonSelector from "../components/StandingsSeasonSelector";

// ── Status label mapping ─────────────────────────────────────────────────────
const getStatusLabel = (status) => {
  const labels = {
    registration: "Registration Open",
    active: "Regular Season",
    playoffs: "Playoffs",
    completed: "Season Complete",
    upcoming: "Upcoming Season",
  };
  return labels[status] || "Season";
};

// ── Tiebreaker detection ─────────────────────────────────────────────────────
const hasTie = (a, b) =>
  a && b && parseFloat(a.win_pct ?? 0) === parseFloat(b.win_pct ?? 0);

// ── Shared sub-components ────────────────────────────────────────────────────

const Last5Dots = ({ dots }) => (
  <div className="flex items-center gap-1">
    {dots.map((result, i) => (
      <div
        key={i}
        className={`w-2.5 h-2.5 rounded-full ${
          result === "W"
            ? "bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]"
            : "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.4)]"
        }`}
        title={result === "W" ? "Win" : "Loss"}
      />
    ))}
    {dots.length === 0 && (
      <span className="text-xs text-gray-500 italic">—</span>
    )}
  </div>
);

const TeamLogo = ({ team, ts, isTopSeed, sizeClass = "w-10 h-10" }) => (
  <div className="relative shrink-0">
    {team.logo_url ? (
      <img
        src={team.logo_url}
        alt={team.name}
        className={`${sizeClass} rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 object-cover`}
      />
    ) : (
      <div
        className={`${sizeClass} rounded-lg flex items-center justify-center text-sm font-black text-white shadow-sm`}
        style={ts.headerStyle}
      >
        {team.name
          .split(" ")
          .map((w) => w[0])
          .join("")
          .slice(0, 2)
          .toUpperCase()}
      </div>
    )}
    {isTopSeed && (
      <div
        className="absolute -top-1 -right-1 w-3 h-3 border-2 border-white dark:border-gray-900 rounded-full"
        style={{ background: ts.accentHex }}
        title="Playoff Spot"
      />
    )}
  </div>
);

const TieBadge = () => (
  <span
    className="inline-flex items-center text-[9px] font-black text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded px-1"
    title="Tiebreaker applied: 1) Head-to-head record → 2) H2H point diff → 3) Overall point diff"
  >
    TB
  </span>
);

// ── Main Standings component ─────────────────────────────────────────────────

const Standings = () => {
  const navigate = useNavigate();
  const { selectedSeason } = useSeason();
  const seasonName = selectedSeason?.name || "No Season Selected";
  const seasonStatus = getStatusLabel(selectedSeason?.status);
  const { data: teams, loading } = useStandings(selectedSeason?.id);
  const [activeTab, setActiveTab] = useState("standings");

  // streak is oldest→newest (SQL string_agg ORDER BY game_date ASC)
  const last5 = (team) => (team.streak ?? "").split("").filter(Boolean);

  const header = (
    <div className="bg-orange-600 dark:bg-gray-900">
      <StandingsSeasonSelector />
      <div className="p-6">
        <h1 className="text-3xl font-black italic uppercase tracking-tighter text-white">
          League Standings
        </h1>
        <p className="text-gray-100 dark:text-gray-400 text-sm font-medium">
          {seasonName} · {seasonStatus}
        </p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-800">
        {header}
        <div className="text-center py-20 text-gray-400 dark:text-gray-500 animate-pulse text-sm">
          Loading standings...
        </div>
      </div>
    );
  }

  const TabToggle = () => (
    <div className="flex gap-1 p-3 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/30">
      {[
        { key: "standings", label: "Standings" },
        { key: "bracket", label: "Playoff Picture" },
      ].map(({ key, label }) => (
        <button
          key={key}
          onClick={() => setActiveTab(key)}
          className={`px-4 py-1.5 rounded-full text-sm font-bold transition-all ${
            activeTab === key
              ? "bg-orange-600 text-white shadow-sm"
              : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  // ── Mobile card list ───────────────────────────────────────────────────────

  const MobileList = () => (
    <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-800">
      {teams.map((team, idx) => {
        const isTopSeed = idx < 8;
        const dots = last5(team);

        // 1. SAFELY EXTRACT NESTED RELATION DATA
        // Check if team_profiles exists, and extract the first item [0]
        const profile = team?.team_profiles?.[0] || team?.team_profile || {};

        // 2. MERGE THE PROFILE FIELDS INTO A SYNTHETIC OBJECT
        const teamWithColors = {
          ...team,
          primary_color: profile.primary_color || team.primary_color,
          secondary_color: profile.secondary_color || team.secondary_color,
          gradient: profile.gradient ?? team.gradient ?? false,
        };

        // 3. PASS THE MERGED OBJECT TO THE COLOR UTILITY
        const ts = getTeamStyle(teamWithColors);
        const tied =
          hasTie(teamWithColors, teams[idx + 1]) ||
          hasTie(teamWithColors, teams[idx - 1]);

        const winPct =
          team.win_pct != null
            ? `${parseFloat(team.win_pct).toFixed(0)}%`
            : "—";
        const diff = team.point_diff ?? 0;

        return (
          <div
            key={team.id}
            onClick={() => navigate(`/teams/${team.id}`)}
            className="flex items-center gap-3 px-4 py-3 cursor-pointer active:bg-gray-50 dark:active:bg-gray-800/40 transition-colors"
          >
            <div
              className={`flex-none flex items-center justify-center w-7 h-7 rounded-full font-bold text-xs
                ${isTopSeed ? "text-white" : "text-gray-400 bg-gray-100 dark:bg-gray-800"}`}
              style={isTopSeed ? ts.accentStyle : undefined}
            >
              {team.rank}
            </div>
            <div
              className="flex-none w-1 self-stretch rounded-full min-h-[40px]"
              style={ts.accentStyle}
            />
            <TeamLogo
              team={team}
              ts={ts}
              isTopSeed={isTopSeed}
              sizeClass="w-9 h-9"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="font-bold text-sm text-gray-900 dark:text-white truncate leading-tight">
                  {team.name}
                </p>
                {tied && <TieBadge />}
              </div>
              <div className="flex items-center gap-1.5 mt-1">
                <Last5Dots dots={dots} />
              </div>
            </div>
            <div className="flex-none text-right">
              <p className="font-mono font-bold text-sm text-gray-700 dark:text-gray-300 leading-tight">
                {team.wins}–{team.losses}
              </p>
              <p
                className={`text-xs font-bold mt-0.5
                ${
                  (team.win_pct ?? 0) >= 50
                    ? "text-green-600 dark:text-green-400"
                    : "text-gray-400 dark:text-gray-500"
                }`}
              >
                {winPct}
              </p>
            </div>
            <div
              className={`flex-none w-10 text-right text-xs font-bold
              ${diff > 0 ? "text-blue-500" : "text-gray-400"}`}
            >
              {diff > 0 ? `+${diff}` : diff === 0 ? "—" : diff}
            </div>
            <svg
              className="flex-none w-4 h-4 text-gray-300 dark:text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        );
      })}
    </div>
  );

  // ── Desktop table ──────────────────────────────────────────────────────────

  const DesktopTable = () => (
    <div className="hidden md:block overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-800/50 border-b dark:border-gray-700">
            <th className="text-left p-4 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 font-bold">
              Rank
            </th>
            <th className="text-left p-4 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 font-bold">
              Team
            </th>
            <th className="text-center p-4 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 font-bold font-mono">
              W – L
            </th>
            <th className="text-center p-4 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 font-bold">
              Win %
            </th>
            <th className="text-center p-4 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 font-bold">
              GB
            </th>
            <th className="text-center p-4 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 font-bold">
              Diff
            </th>
            <th className="text-center p-4 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 font-bold">
              Last 5
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {teams.map((team, idx) => {
            const isTopSeed = idx < 8;
            const dots = last5(team);

            // 1. SAFELY EXTRACT NESTED RELATION DATA
            // Check if team_profiles exists, and extract the first item [0]
            const profile =
              team?.team_profiles?.[0] || team?.team_profile || {};

            // 2. MERGE THE PROFILE FIELDS INTO A SYNTHETIC OBJECT
            const teamWithColors = {
              ...team,
              primary_color: profile.primary_color || team.primary_color,
              secondary_color: profile.secondary_color || team.secondary_color,
              gradient: profile.gradient ?? team.gradient ?? false,
            };

            // 3. PASS THE MERGED OBJECT TO THE COLOR UTILITY
            const ts = getTeamStyle(teamWithColors);
            const tied =
              hasTie(teamWithColors, teams[idx + 1]) ||
              hasTie(teamWithColors, teams[idx - 1]);

            return (
              <tr
                key={team.id}
                onClick={() => navigate(`/teams/${team.id}`)}
                className="group cursor-pointer transition-all duration-150 hover:bg-gray-50 dark:hover:bg-gray-800/40"
              >
                {/* Rank */}
                <td className="p-4">
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm
                      ${isTopSeed ? "text-white" : "text-gray-400"}`}
                    style={isTopSeed ? ts.accentStyle : undefined}
                  >
                    {team.rank}
                  </div>
                </td>

                {/* Team Name — left color bar + name */}
                <td className="p-4">
                  <div className="flex items-center gap-3 h-9">
                    {/* Left color bar indicating team color */}
                    <div
                      className="w-1.5 h-full rounded-full shrink-0"
                      style={ts.accentStyle}
                    />
                    <TeamLogo team={team} ts={ts} isTopSeed={isTopSeed} />
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-900 dark:text-white transition-colors group-hover:opacity-80">
                        {team.name}
                      </span>
                      {tied && <TieBadge />}
                    </div>
                  </div>
                </td>

                {/* Record */}
                <td className="text-center p-4">
                  <span className="font-mono font-bold text-gray-700 dark:text-gray-300">
                    {team.wins} – {team.losses}
                  </span>
                </td>

                {/* Win % */}
                <td className="text-center p-4">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-black
                    ${
                      (team.win_pct ?? 0) >= 50
                        ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                        : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                    }`}
                  >
                    {team.win_pct != null
                      ? `${parseFloat(team.win_pct).toFixed(0)}%`
                      : "—"}
                  </span>
                </td>

                {/* Games Behind */}
                <td className="text-center p-4 text-sm font-semibold text-gray-500 dark:text-gray-400">
                  {team.games_behind === 0 || team.games_behind == null
                    ? "—"
                    : team.games_behind}
                </td>

                {/* Point Diff */}
                <td
                  className={`text-center p-4 font-bold
                  ${(team.point_diff ?? 0) > 0 ? "text-blue-500" : "text-gray-400"}`}
                >
                  {(team.point_diff ?? 0) > 0
                    ? `+${team.point_diff}`
                    : team.point_diff}
                </td>

                {/* Last 5 games */}
                <td className="p-4">
                  <div className="flex justify-center gap-1">
                    <Last5Dots dots={dots} />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-800">
      {header}
      <TabToggle />
      {teams.length === 0 ? (
        <div className="text-center py-20 bg-gray-50 dark:bg-gray-800/20 italic text-gray-500 dark:text-gray-400">
          The court is empty... for now.
        </div>
      ) : activeTab === "standings" ? (
        <>
          <MobileList />
          <DesktopTable />
        </>
      ) : (
        <PlayoffBracket teams={teams} seasonId={selectedSeason?.id} />
      )}
    </div>
  );
};

export default Standings;
